import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, Pencil, Trash2, Eye } from 'lucide-react';
import { api } from './api';

const STATUS_LABEL = { draft: 'Entwurf', published: 'Veröffentlicht', archived: 'Archiviert' };

export default function RecipesList() {
  const [recipes, setRecipes] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('-updated_at');
  const [confirmId, setConfirmId] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    params.set('sort', sort);
    api(`/admin/recipes?${params}`).then((d) => setRecipes(d.recipes)).catch((e) => setError(e.message));
  };

  useEffect(load, [q, status, sort]);

  const duplicate = async (id) => {
    const copy = await api(`/admin/recipes/${id}/duplicate`, { method: 'POST' });
    navigate(`/admin/rezepte/${copy.id}`);
  };

  const remove = async (id) => {
    await api(`/admin/recipes/${id}`, { method: 'DELETE' });
    setConfirmId(null);
    load();
  };

  const setRecipeStatus = async (id, s) => {
    await api(`/admin/recipes/${id}/status`, { method: 'POST', body: JSON.stringify({ status: s }) });
    load();
  };

  return (
    <div data-testid="admin-recipes-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-bold">Rezepte</h1>
        <Link to="/admin/rezepte/neu" className="btn-primary" data-testid="recipes-new-button">Neues Rezept</Link>
      </div>

      {error && <p role="alert" className="mt-4 rounded-sm border border-red-700 bg-red-50 p-4 font-semibold text-red-800">{error}</p>}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <label htmlFor="recipes-search" className="mb-1 block text-sm font-semibold">Suche</label>
          <input id="recipes-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} className="min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3" data-testid="recipes-search" />
        </div>
        <div>
          <label htmlFor="recipes-status" className="mb-1 block text-sm font-semibold">Status</label>
          <select id="recipes-status" value={status} onChange={(e) => setStatus(e.target.value)} className="min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3 sm:w-44" data-testid="recipes-status-filter">
            <option value="">Alle</option>
            <option value="draft">Entwurf</option>
            <option value="published">Veröffentlicht</option>
            <option value="archived">Archiviert</option>
          </select>
        </div>
        <div>
          <label htmlFor="recipes-sort" className="mb-1 block text-sm font-semibold">Sortierung</label>
          <select id="recipes-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3 sm:w-52" data-testid="recipes-sort">
            <option value="-updated_at">Zuletzt geändert</option>
            <option value="title">Titel A–Z</option>
            <option value="-veroeffentlicht_am">Neueste zuerst</option>
            <option value="kategorie">Kategorie</option>
          </select>
        </div>
      </div>

      {!recipes ? (
        <p role="status" className="mt-8 text-brand-brown-soft">Lade Rezepte …</p>
      ) : recipes.length === 0 ? (
        <p className="mt-8 text-brand-brown-soft">Keine Rezepte gefunden. Lege dein erstes Rezept an.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {recipes.map((r) => (
            <li key={r.id} className="border border-brand-line bg-brand-cream p-4 sm:p-5" data-testid={`recipe-row-${r.slug}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-serif text-xl font-bold">{r.title}</p>
                  <p className="text-sm text-brand-brown-soft">
                    /rezepte/{r.slug} · {r.kategorie} · {STATUS_LABEL[r.status]} · geändert {new Date(r.updated_at).toLocaleDateString('de-CH')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/admin/rezepte/${r.id}`} className="inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid={`edit-${r.slug}`}>
                    <Pencil className="h-4 w-4" aria-hidden="true" /> Bearbeiten
                  </Link>
                  <Link to={`/rezepte/${r.slug}?vorschau=admin`} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid={`preview-${r.slug}`}>
                    <Eye className="h-4 w-4" aria-hidden="true" /> Vorschau
                  </Link>
                  <button type="button" onClick={() => duplicate(r.id)} className="inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid={`duplicate-${r.slug}`}>
                    <Copy className="h-4 w-4" aria-hidden="true" /> Duplizieren
                  </button>
                  {r.status === 'published' ? (
                    <button type="button" onClick={() => setRecipeStatus(r.id, 'draft')} className="inline-flex min-h-11 items-center rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid={`unpublish-${r.slug}`}>
                      Zurückziehen
                    </button>
                  ) : (
                    <button type="button" onClick={() => setRecipeStatus(r.id, 'published')} className="inline-flex min-h-11 items-center rounded-sm bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:bg-brand-olive-dark" data-testid={`publish-${r.slug}`}>
                      Veröffentlichen
                    </button>
                  )}
                  {confirmId === r.id ? (
                    <span className="inline-flex items-center gap-2 rounded-sm border border-red-700 bg-red-50 px-3 py-2 text-sm" role="alert">
                      Wirklich löschen?
                      <button type="button" onClick={() => remove(r.id)} className="font-bold text-red-800 underline underline-offset-2" data-testid={`confirm-delete-${r.slug}`}>Ja, löschen</button>
                      <button type="button" onClick={() => setConfirmId(null)} className="font-semibold underline underline-offset-2">Abbrechen</button>
                    </span>
                  ) : (
                    <button type="button" onClick={() => setConfirmId(r.id)} className="inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-red-700 px-3 py-2 text-sm font-semibold text-red-800 hover:bg-red-50" data-testid={`delete-${r.slug}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> Löschen
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
